/**
 * Voice mode experiment, behind localStorage sg_copilot_voice = "1".
 *
 * Speech in: the browser's Web Speech API (Chrome). Interim words land in
 * the composer as they are recognised; a final result with a pause ends the
 * utterance and the caller sends it as a normal turn.
 *
 * Speech out: the agent's /copilot/tts (Deepgram Aura) when it is
 * configured, else speechSynthesis with Chrome's Google voice. Never reads
 * the full markdown: sentences as they stream, plus the open question.
 */
import { agentBaseUrl, ensureCopilotCredential } from "./SseAgentTransport";
import { getPrefs } from "./copilotPrefs";

export const voiceEnabled = () => {
  try {
    return localStorage.getItem("sg_copilot_voice") === "1";
  } catch (e) {
    return false;
  }
};

const Recognition =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export const voiceSupported = () => Boolean(Recognition) && typeof window !== "undefined" && "speechSynthesis" in window;

/**
 * Start listening. onInterim(text) fires as words come in, onFinal(text)
 * once the utterance ends. Returns a stop() function.
 */

// ── mic meter ─────────────────────────────────────────────────────────────
// One echo-cancelled mic stream, metered continuously while a session or
// dictation runs. Two numbers come out of it: the noise floor (quiet-frame
// average) and the loudest frame in the last `windowMs`. Near-field speech,
// someone at the mic, sits far above a conversation across the room, so a
// transcript that arrived without a loud frame is somebody else's.
const meter = { stream: null, ctx: null, floor: 0.01, userLevel: 0, peaks: [], raf: 0, users: 0 };
const METER_WINDOW_MS = 1500;
const FLOOR_WINDOW_MS = 3000;
const NEAR_GAIN = 4; // over the floor: loud enough to be the user
const BARGE_GAIN = 6; // louder still, sustained, to interrupt the copilot
const NEAR_MIN = 0.045;
const FLOOR_MAX = 0.02; // a room louder than this is noise, not the floor
const USER_SHARE = 0.45; // of the calibrated user level
const SENSITIVITY = { low: 1.4, normal: 1, high: 0.7 };
const sensitivity = () => SENSITIVITY[getPrefs().micSensitivity] || 1;
export const resetMeter = () => { meter.floor = 0.01; meter.userLevel = 0; meter.peaks = []; };

const meterAcquire = () => {
  meter.users += 1;
  if (meter.stream || !navigator.mediaDevices) return;
  navigator.mediaDevices
    .getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false } })
    .then(stream => {
      if (!meter.users) { stream.getTracks().forEach(t => t.stop()); return; }
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(an);
      const buf = new Uint8Array(an.fftSize);
      meter.stream = stream;
      meter.ctx = ctx;
      const tick = () => {
        if (!meter.stream) return;
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) { const d = (buf[i] - 128) / 128; sum += d * d; }
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();
        meter.peaks.push([now, rms]);
        while (meter.peaks.length && meter.peaks[0][0] < now - FLOOR_WINDOW_MS) meter.peaks.shift();
        // Floor = the quiet fifth of the last 3s, capped: background talk
        // must never become "the floor" or the user's own voice gets rejected.
        if (meter.peaks.length > 30 && (meter.peaks.length & 7) === 0) {
          const sorted = meter.peaks.map(p => p[1]).sort((a, b) => a - b);
          meter.floor = Math.min(FLOOR_MAX, sorted[Math.floor(sorted.length * 0.2)]);
        }
        meter.raf = requestAnimationFrame(tick);
      };
      tick();
    })
    .catch(() => {});
};
const meterRelease = () => {
  meter.users = Math.max(0, meter.users - 1);
  if (meter.users || !meter.stream) return;
  cancelAnimationFrame(meter.raf);
  meter.stream.getTracks().forEach(t => t.stop());
  if (meter.ctx) meter.ctx.close().catch(() => {});
  meter.stream = null;
  meter.ctx = null;
  meter.peaks = [];
};
/** 0..1 loudness for the UI orb: the user's own level maps to ~1. */
export const micLevel = () => {
  if (!meter.stream) return 0;
  const peak = recentPeak(180);
  const top = Math.max(meter.userLevel || 0, NEAR_MIN * 2);
  return Math.max(0, Math.min(1, (peak - meter.floor) / top));
};

const recentPeak = (ms = METER_WINDOW_MS) => {
  const since = Date.now() - ms;
  let peak = 0;
  for (const [t, rms] of meter.peaks) if (t >= since && rms > peak) peak = rms;
  return peak;
};
const nearThreshold = () =>
  Math.max(NEAR_MIN, meter.floor * NEAR_GAIN, meter.userLevel * USER_SHARE) * sensitivity();
/** Did the mic get near-field loud in the last window? Unknown (no meter) counts as yes. */
const heardNearField = () => {
  if (!meter.stream || !getPrefs().nearFieldOnly) return true;
  return recentPeak() >= nearThreshold();
};
/** An utterance was accepted: remember how loud the user is. */
const calibrateUser = () => {
  const peak = recentPeak(FLOOR_WINDOW_MS);
  if (peak < NEAR_MIN) return;
  meter.userLevel = meter.userLevel ? meter.userLevel * 0.7 + peak * 0.3 : peak;
};
/** Sustained near-field energy right now (for barge-in): ~6 of the last frames. */
const nearFieldNow = () => {
  if (!meter.stream) return false;
  const t = Math.max(NEAR_MIN * 1.4, meter.floor * BARGE_GAIN, meter.userLevel * 0.6) * sensitivity();
  // ~250ms of sustained voice at the user's level, not a cough or a door.
  const recent = meter.peaks.slice(-16);
  return recent.length >= 14 && recent.filter(([, rms]) => rms >= t).length >= 13;
};

export const listen = ({ onInterim, onFinal, onEnd, lang = getPrefs().sttLang, idleMs = 0 }) => {
  if (!Recognition) return () => {};
  const rec = new Recognition();
  rec.lang = lang;
  // Continuous: Chrome would otherwise end the utterance at the first ~1s
  // pause, mid-thought. We decide the end ourselves: quiet for `pauseMs`
  // after the last words (interim or final) means they are done.
  rec.continuous = true;
  rec.interimResults = true;
  rec.maxAlternatives = 1;
  const pauseMs = getPrefs().pauseMs || 2000;
  let finalText = "";
  let interimText = "";
  let timer = null;
  let watchdog = null;
  let lastResultAt = Date.now();
  let done = false;
  meterAcquire();
  const finish = () => {
    if (done) return;
    done = true;
    meterRelease();
    clearTimeout(timer);
    clearInterval(watchdog);
    const text = (finalText + " " + interimText).replace(/\s+/g, " ").trim();
    try { rec.stop(); } catch (e) { /* already stopped */ }
    if (text) calibrateUser();
    if (text && onFinal) onFinal(text);
    if (onEnd) onEnd();
  };
  // How long to wait depends on how the sentence ends: a finished thought
  // goes sooner, a trailing word ("find me the", "and", "um") gets time.
  const waitFor = text => {
    const t = text.trim();
    if (/[.?!]$/.test(t)) return pauseMs * 0.6;
    if (/(,|\b(and|or|but|so|then|the|a|an|to|for|with|at|in|of|on|from|um|uh|like|about|me|my|find|show|get|add))$/i.test(t)) return pauseMs * 2;
    return pauseMs;
  };
  const armTimer = () => {
    clearTimeout(timer);
    timer = setTimeout(finish, waitFor(finalText + interimText));
  };
  // Chrome's continuous recognition can go quiet without ending. If the
  // meter keeps hearing the user and nothing comes back for 8s, restart.
  watchdog = setInterval(() => {
    if (done) return;
    // idleMs: nothing at all heard since start (barge-in false alarm).
    if (idleMs && !finalText && !interimText && Date.now() - lastResultAt > idleMs) { finish(); return; }
    if (Date.now() - lastResultAt > 8000 && meter.stream && recentPeak(5000) >= nearThreshold()) finish();
  }, 500);
  rec.onresult = event => {
    // Words that arrived while nobody was near the mic are background
    // conversation: the recogniser heard them, the user did not say them.
    lastResultAt = Date.now();
    if (!heardNearField()) return;
    interimText = "";
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const r = event.results[i];
      if (r.isFinal) finalText += r[0].transcript + " ";
      else interimText += r[0].transcript;
    }
    if (onInterim) onInterim((finalText + interimText).replace(/\s+/g, " ").trim());
    // Every new word restarts the clock; silence lets it run out.
    if (finalText.trim() || interimText.trim()) armTimer();
  };
  rec.onspeechend = () => { if (finalText.trim() || interimText.trim()) armTimer(); };
  rec.onerror = () => finish();
  // Chrome stops continuous recognition on its own after long silence or
  // ~60s; treat that as the end of whatever was said.
  rec.onend = () => finish();
  try {
    rec.start();
  } catch (e) {
    finish();
  }
  return () => finish();
};

let speaking = null;
// The voice list loads asynchronously in Chrome: an early getVoices() is
// empty and the fallback picked a different voice each turn. Choose once,
// when the list is in, and keep it for the life of the page.
let lockedVoice = null;
// Chrome ships neural cloud voices under "Google …"; they beat every local
// macOS/Windows voice. Same list order on Edge (falls through to Microsoft Online).
// Apple's downloadable Premium/Enhanced voices (System Settings >
// Accessibility > Spoken Content > Manage Voices) beat Google's when present.
const preferredVoices = [
  "Zoe (Premium)",
  "Ava (Premium)",
  "Evan (Premium)",
  "Samantha (Enhanced)",
  "Google US English",
  "Google UK English Female",
  "Microsoft Aria Online (Natural) - English (United States)",
  "Microsoft Jenny Online (Natural) - English (United States)",
  "Samantha",
];
const resolveVoice = () => {
  if (lockedVoice) return lockedVoice;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;
  // A/B by name without a rebuild: localStorage sg_copilot_voice_name.
  let wanted = "";
  try { wanted = localStorage.getItem("sg_copilot_voice_name") || ""; } catch (e) { /* no storage */ }
  if (wanted) {
    const v = voices.find(x => x.name === wanted || x.name.startsWith(wanted));
    if (v) { lockedVoice = v; return v; }
  }
  for (const name of preferredVoices) {
    const v = voices.find(x => x.name === name);
    if (v) { lockedVoice = v; return v; }
  }
  lockedVoice = voices.find(v => /^en/i.test(v.lang)) || voices[0];
  return lockedVoice;
};
if (typeof window !== "undefined" && "speechSynthesis" in window) {
  window.speechSynthesis.onvoiceschanged = () => resolveVoice();
  resolveVoice();
}

const speechListeners = new Set();
/** Subscribe to "speaking started/ended"; used to resume the mic after a reply. */
export const onSpeech = fn => { speechListeners.add(fn); return () => speechListeners.delete(fn); };
const notify = state => speechListeners.forEach(fn => { try { fn(state); } catch (e) { /* listener */ } });

// speechSynthesis has no SSML in Chrome: rate / pitch / volume per
// utterance is the whole instrument. One utterance per phrase, each tuned by
// what it is, queued back to back (the engine keeps order).
const phraseTone = (phrase, i, n) => {
  // Small moves only: big pitch steps between utterances are what sounds
  // synthetic. Rate drifts a little per sentence so it is not metronomic.
  const t = { rate: 0.98 + ((i * 7) % 5) * 0.01, pitch: 1 };
  if (/\?$/.test(phrase)) t.pitch = 1.05; // question lifts
  else if (/^(sorry|couldn't|can't|failed|no )/i.test(phrase)) { t.rate -= 0.04; t.pitch = 0.96; }
  if (i === n - 1 && n > 1 && !/\?$/.test(phrase)) t.pitch -= 0.02; // settle at the end
  return t;
};

// Written for the eye, read for the ear: acronyms letter by letter, money
// and big numbers in words, markdown gone.
const ACRONYMS = { SaaS: "sass", VP: "V P", VPs: "V Ps", SDR: "S D R", SDRs: "S D Rs", CRM: "C R M", ICP: "I C P", US: "U S", UK: "U K", CEO: "C E O", CTO: "C T O", CFO: "C F O", GTM: "go to market", ARR: "A R R", MQL: "M Q L", SQL: "S Q L" };
export const forSpeech = text =>
  String(text || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`#>|]/g, "")
    .replace(/\$(\d+(?:\.\d+)?)([kKmM])\b/g, (m, n, u) => `${n} ${/k/i.test(u) ? "thousand" : "million"} dollars`)
    .replace(/\$(\d[\d,]*)/g, "$1 dollars")
    .replace(/\b(\d{1,3}),(\d{3}),(\d{3})\b/g, (m, a) => `about ${a} million`)
    .replace(/\b(\d{1,3}),(\d{3})\b/g, (m, a, b) => (Number(a) >= 10 ? `about ${a} thousand` : `${a} thousand ${Number(b) ? b.replace(/^0+/, "") : ""}`))
    .replace(/\b(SaaS|VPs?|SDRs?|CRM|ICP|US|UK|CEO|CTO|CFO|GTM|ARR|MQL|SQL)\b/g, m => ACRONYMS[m] || m)
    .replace(/\s+/g, " ")
    .trim();

let pending = 0; // utterances queued but not finished
// cancel() makes Chrome fire onend for the utterances it threw away, after
// the next ones are already queued; each generation ignores the last one's.
let generation = 0;
const utter = (phrase, tone) => {
  // Two voices at once is worse than the wrong voice: whatever is playing
  // stops before the browser voice starts.
  if (playing) {
    const a = playing;
    playing = null;
    a.pause();
    a.src = "";
  }
  const u = new SpeechSynthesisUtterance(phrase);
  const v = resolveVoice();
  if (v) u.voice = v;
  Object.assign(u, tone);
  const gen = generation;
  pending += 1;
  if (!speaking) {
    speaking = u;
    notify("started");
  }
  u.onend = () => {
    if (gen !== generation) return;
    pending -= 1;
    if (pending <= 0) { pending = 0; speaking = null; notify("ended"); }
  };
  u.onerror = u.onend;
  window.speechSynthesis.speak(u);
};

// ── clip cache ────────────────────────────────────────────────────────────
// Fillers, acknowledgements and step labels come from a fixed pool of short
// lines and are billed per character every time they are spoken. Those are
// marked `repeated` by their callers and cached by voice + text in
// IndexedDB: paid for once per browser instead of once per session. Reply
// sentences are said once, so they stream from the server and are not stored.
const CACHE_MAX_CHARS = 90;
const DB_NAME = "sgCopilotVoice";
const STORE = "clips";
const memCache = new Map();
let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise(resolve => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch (e) {
      resolve(null); // private mode, blocked storage: memory cache still works
    }
  });
  return dbPromise;
};

const cacheKey = (text, voice) => `${voice}|${text}`;

const cacheGet = async key => {
  if (memCache.has(key)) return memCache.get(key);
  const db = await openDb();
  if (!db) return null;
  return new Promise(resolve => {
    try {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
      req.onsuccess = () => {
        if (req.result) memCache.set(key, req.result);
        resolve(req.result || null);
      };
      req.onerror = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
};

const cachePut = async (key, blob) => {
  memCache.set(key, blob);
  const db = await openDb();
  if (!db) return;
  try {
    db.transaction(STORE, "readwrite").objectStore(STORE).put(blob, key);
  } catch (e) {
    // ponytail: no eviction. ~200 short clips is a few MB; add an LRU only
    // if the pool ever stops being finite.
  }
};

// ── server voice: one fetch per sentence, played in order, next one
// fetched while the current plays. A failed fetch drops to the browser
// voice for the rest of the session.
// A failure must not cost the session its voice: fall back for that line
// only and try the server again after a cooldown. The first line of a page
// used to fail (token not minted yet) and everything after it was Chrome.
let serverVoiceOffUntil = 0;
const SERVER_VOICE_COOLDOWN_MS = 60000;
const serverVoiceUp = () => Date.now() >= serverVoiceOffUntil;
const serverVoiceDown = why => {
  serverVoiceOffUntil = Date.now() + SERVER_VOICE_COOLDOWN_MS;
  // eslint-disable-next-line no-console
  console.warn(`[copilot voice] server voice unavailable (${why}); using the browser voice for 60s`);
};
const queue = []; // { text, audio }
let playing = null;
let playerGen = 0;

// The copilot token lives in memory (minted once by the transport); resolve
// it before the audio element gets its URL.
let credential = "";
/** Resolve the copilot token, then run fn. Skips fn when there is no token. */
export const withCredential = fn => {
  if (credential) { fn(credential); return; }
  ensureCopilotCredential().then(token => {
    credential = token || "";
    if (token) fn(token);
    else fn(null);
  });
};
export const ttsUrl = (text, token, voice = getPrefs().voice) =>
  `${agentBaseUrl()}/copilot/tts?text=${encodeURIComponent(text)}&voice=${encodeURIComponent(voice)}&key=${encodeURIComponent(token)}`;

const prime = item => {
  if (item.audio) return;
  const a = new Audio();
  a.preload = "auto";
  a.playbackRate = getPrefs().speed === "fast" ? 1.12 : 1;
  item.audio = a;
  const voice = getPrefs().voice;
  // Short, repeated lines go through the cache (one fetch ever); anything
  // longer streams as it is produced, which is what keeps replies prompt.
  if (item.repeated && item.text.length <= CACHE_MAX_CHARS) {
    const key = cacheKey(item.text, voice);
    cacheGet(key).then(hit => {
      if (item.audio !== a) return;
      if (hit) {
        item.objectUrl = URL.createObjectURL(hit);
        a.src = item.objectUrl;
        return;
      }
      withCredential(token => {
        if (item.audio !== a) return;
        if (!token) { item.noToken = true; return; }
        fetch(ttsUrl(item.text, token, voice))
          .then(r => (r.ok ? r.blob() : Promise.reject(new Error(`tts_${r.status}`))))
          .then(blob => {
            cachePut(key, blob);
            if (item.audio !== a) return;
            item.objectUrl = URL.createObjectURL(blob);
            a.src = item.objectUrl;
          })
          .catch(() => { if (item.audio === a) item.fetchFailed = true; });
      });
    });
    return;
  }
  withCredential(token => {
    if (item.audio !== a) return;
    if (!token) { item.noToken = true; return; }
    a.src = ttsUrl(item.text, token, voice);
  });
};

const playNext = () => {
  const item = queue.shift();
  if (!item) {
    playing = null;
    notify("ended");
    return;
  }
  const gen = playerGen;
  prime(item);
  if (queue[0]) prime(queue[0]);
  const a = item.audio;
  playing = a;
  a.onended = () => {
    if (item.objectUrl) { URL.revokeObjectURL(item.objectUrl); item.objectUrl = null; }
    if (gen === playerGen) playNext();
  };
  const fallback = why => {
    if (gen !== playerGen) return;
    // A single sentence failing is usually a blip or a concurrency limit,
    // not an outage: retry it once on the server before changing voice
    // mid-reply, which is what made the voice swap halfway through.
    if (!item.retried) {
      item.retried = true;
      item.audio = null;
      item.noToken = false;
      playing = null;
      queue.unshift(item);
      setTimeout(() => { if (gen === playerGen) playNext(); }, 250);
      return;
    }
    serverVoiceDown(why);
    const rest = [item.text, ...queue.map(q => q.text)];
    queue.length = 0;
    playing = null;
    rest.forEach((t, i, all) => utter(t, phraseTone(t, i, all.length)));
  };
  a.onerror = () => fallback(a.src ? "audio error" : "no source");
  withCredential(token => {
    if (gen !== playerGen) return;
    if (item.fetchFailed) { fallback("tts fetch failed"); return; }
    if (!token || item.noToken) { fallback("no copilot token"); return; }
    const start = () =>
      a.play().catch(err => fallback(err && err.name === "NotAllowedError" ? "autoplay blocked" : "play failed"));
    // src is resolved asynchronously for cached clips; wait rather than
    // calling play() on an empty element, which fails as "no source".
    if (a.src) start();
    else {
      let waited = 0;
      const poll = setInterval(() => {
        if (gen !== playerGen) { clearInterval(poll); return; }
        if (item.fetchFailed) { clearInterval(poll); fallback("tts fetch failed"); return; }
        if (a.src) { clearInterval(poll); start(); return; }
        waited += 50;
        if (waited > 6000) { clearInterval(poll); fallback("tts timeout"); }
      }, 50);
    }
  });
};

const enqueueServer = (phrases, repeated) => {
  phrases.forEach(text => queue.push({ text, audio: null, repeated }));
  if (!playing) {
    notify("started");
    playNext();
  } else if (queue[0]) {
    prime(queue[0]);
  }
};

/**
 * Add to what is being said; used while a reply streams in.
 * `repeated` marks a line that comes from a fixed pool (fillers, step
 * labels): only those are worth storing, since a reply sentence is said once.
 */
export const enqueue = (text, repeated = false) => {
  if (!text || typeof window === "undefined") return;
  const line = forSpeech(text);
  if (!line) return;
  // One utterance per sentence; the engine phrases commas better than a
  // hard stop between utterances does.
  const phrases = (line.match(/[^.!?]+[.!?]+["')\]]*|[^.!?]+$/g) || [line]).map(p => p.trim()).filter(Boolean);
  if (serverVoiceUp()) {
    enqueueServer(phrases, repeated);
    return;
  }
  if (!("speechSynthesis" in window)) return;
  phrases.forEach((phrase, i) => utter(phrase, phraseTone(phrase, i, phrases.length)));
};

export const speak = (text, repeated = false) => {
  stopSpeaking();
  enqueue(text, repeated);
};

/** Pause what is playing without losing the rest; resumeSpeaking() continues. */
let pausedQueue = null;
export const pauseSpeaking = () => {
  if (pausedQueue) return;
  if (playing) {
    const a = playing;
    a.pause();
    pausedQueue = { audio: a, rest: queue.splice(0), gen: playerGen };
    return;
  }
  if (speaking && "speechSynthesis" in window) {
    window.speechSynthesis.pause();
    pausedQueue = { synth: true };
  }
};
export const resumeSpeaking = () => {
  const p = pausedQueue;
  pausedQueue = null;
  if (!p) return;
  if (p.synth) { window.speechSynthesis.resume(); return; }
  if (p.gen !== playerGen) return; // something else took over meanwhile
  queue.push(...p.rest);
  playing = p.audio;
  p.audio.play().catch(() => playNext());
};
export const isSpeaking = () => Boolean(speaking || playing);

export const stopSpeaking = () => {
  if (typeof window === "undefined") return;
  const was = speaking || playing;
  speaking = null;
  pending = 0;
  generation += 1;
  playerGen += 1;
  queue.length = 0;
  pausedQueue = null;
  if (playing) {
    playing.pause();
    playing.src = "";
    playing = null;
  }
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  if (was) notify("ended");
};

/**
 * Continuous conversation. One toggle: while the session is on, the mic
 * listens, sends each utterance, stays quiet while the copilot is talking
 * (so it does not transcribe its own voice), and listens again when the
 * reply has been read. Stops on the toggle or when the tab loses the mic.
 */
let session = null;
export const voiceSessionOn = () => Boolean(session);

// Barge-in. Recognition is off while the copilot talks (it would transcribe
// itself); the mic meter watches for sustained near-field voice instead.
let gate = null;
const startGate = onVoice => {
  if (gate) return;
  meterAcquire();
  gate = { raf: 0 };
  const tick = () => {
    if (!gate) return;
    if (nearFieldNow()) { onVoice(); return; }
    gate.raf = requestAnimationFrame(tick);
  };
  tick();
};
const stopGate = () => {
  if (!gate) return;
  cancelAnimationFrame(gate.raf);
  gate = null;
  meterRelease();
};

export const startVoiceSession = ({ onInterim, onSend, onState }) => {
  if (session) return;
  // UI callbacks live on the session so a new composer (page -> drawer, or
  // a route change) can take them over without restarting the mic.
  resetMeter();
  session = { stop: null, paused: false, ui: { onInterim, onState }, onSend };
  const setState = st => { if (session && session.ui.onState) session.ui.onState(st); };
  // Every accepted utterance goes through here, so "stop" ends the session
  // whether it was said while listening or over a reply.
  const deliver = text => {
    if (!session) return;
    if (isStopPhrase(text)) {
      if (session.onInterrupt) session.onInterrupt();
      stopVoiceSession();
      return;
    }
    session.onSend(text);
  };
  const listenOnce = () => {
    if (!session || session.paused) return;
    setState("listening");
    session.stop = listen({
      onInterim: text => { if (session && session.ui.onInterim) session.ui.onInterim(text); },
      onFinal: deliver,
      onEnd: () => {
        if (!session) return;
        session.stop = null;
        // Recognition ends after every utterance or ~8s of silence; loop
        // unless we are paused for the reply.
        if (!session.paused) setTimeout(listenOnce, 150);
      }
    });
  };
  // Barge-in: pause the reply and listen. Words -> the rest of the reply is
  // dropped and a streaming turn is interrupted. Silence -> it was not for us;
  // the reply resumes where it paused.
  const bargeIn = () => {
    if (!session) return;
    stopGate();
    pauseSpeaking();
    setState("listening");
    let heard = false;
    session.stop = listen({
      idleMs: 3000,
      onInterim: text => {
        if (!heard && text) {
          heard = true;
          stopSpeaking();
          if (session.onInterrupt) session.onInterrupt();
        }
        if (isStopPhrase(text)) { stopVoiceSession(); return; }
        if (session && session.ui.onInterim) session.ui.onInterim(text);
      },
      onFinal: deliver,
      onEnd: () => {
        if (!session) return;
        session.stop = null;
        if (!heard) {
          resumeSpeaking();
          setState("speaking");
          if (getPrefs().bargeIn) startGate(bargeIn);
        }
      }
    });
  };
  const unsub = onSpeech(state => {
    if (!session) return;
    if (state === "started") {
      session.paused = true;
      if (session.stop) session.stop();
      setState("speaking");
      if (getPrefs().bargeIn) startGate(bargeIn);
    } else if (state === "ended") {
      stopGate();
      session.paused = false;
      if (!session.stop) setTimeout(listenOnce, 200);
    }
  });
  session.unsub = unsub;
  listenOnce();
};

/**
 * A newly mounted composer adopts the running session's UI hooks. Returns a
 * token: React can mount the replacement BEFORE unmounting the old composer,
 * so a blind detach on unmount would wipe the live one's hooks — and calling
 * a dead component's setState is the "Should have a queue" crash.
 */
export const attachVoiceUi = ({ onInterim, onState }) => {
  if (!session) return null;
  const token = {};
  session.ui = { onInterim, onState, token };
  if (onState) onState(session.paused ? "speaking" : "listening");
  return token;
};

/** Detach without stopping: the composer is going away, the session is not. */
export const detachVoiceUi = token => {
  if (session && (!token || session.ui.token === token)) session.ui = {};
};

export const stopVoiceSession = () => {
  if (!session) return;
  const s = session;
  session = null;
  if (s.stop) s.stop();
  if (s.unsub) s.unsub();
  stopGate();
  stopSpeaking();
  // The session can end from anywhere -- a spoken "stop", the orchestrator
  // deciding the user signed off -- so it has to tell the composer itself.
  // Without this the waveform and the lit headset stayed on screen.
  if (s.ui && s.ui.onInterim) s.ui.onInterim("");
  if (s.ui && s.ui.onState) s.ui.onState("off");
};

// ── filler ────────────────────────────────────────────────────────────────
// The wait between "sent" and the first words of the reply is dead air in a
// spoken conversation. Built in two halves so a handful of lines makes
// hundreds of openings: a thinking sound or acknowledgement, then what it is
// about to do. Either half can be dropped, so it never falls into a pattern.
const ACKS = [
  "Mm.",
  "Mm-hmm.",
  "Hmm.",
  "Right.",
  "Okay.",
  "Got it.",
  "Sure.",
  "Ah, got it.",
  "Yep.",
  "Makes sense.",
  "Understood.",
  "Alright."
];
const ACTIONS = {
  search: [
    "let me look",
    "let me find those",
    "searching now",
    "let me pull those up",
    "checking who matches",
    "I'll go find them",
    "let me see who is out there",
    "pulling that list now"
  ],
  write: [
    "let me put that together",
    "drafting that now",
    "let me write that",
    "give me a moment to draft it",
    "I'll pull the words together",
    "let me work that out"
  ],
  report: [
    "let me pull the numbers",
    "checking the numbers",
    "let me look at the data",
    "pulling that report now",
    "let me see what it says",
    "checking how that is doing"
  ],
  inbox: [
    "let me check your inbox",
    "looking at your inbox",
    "checking what came in",
    "let me see what is waiting"
  ],
  research: [
    "let me dig into that",
    "researching that now",
    "let me read up on them",
    "give me a second on this one",
    "let me see what I can find on them"
  ],
  generic: [
    "let me check",
    "one second",
    "let me take a look",
    "on it",
    "let me see",
    "give me a moment",
    "let me work that out",
    "checking now"
  ],
  wait: [
    "still on it",
    "almost there",
    "nearly done",
    "bear with me",
    "still working through this",
    "not much longer"
  ]
};
const recent = [];
const pick = list => list[Math.floor(Math.random() * list.length)];
const cap = s2 => s2.charAt(0).toUpperCase() + s2.slice(1);
const pickFiller = kind => {
  const actions = ACTIONS[kind] || ACTIONS.generic;
  for (let i = 0; i < 6; i += 1) {
    // Roughly a third open with a thinking sound, a third acknowledge, a
    // third get straight to it — the mix is what stops it sounding canned.
    const roll = Math.random();
    const action = pick(actions);
    // "Hmm, let me look." runs on; "Got it. Let me look." is two beats.
    const ack = pick(ACKS);
    const runOn = /^(Mm|Mm-hmm|Hmm)\.$/.test(ack);
    const line =
      roll < 0.55
        ? runOn
          ? `${ack.slice(0, -1)}, ${action}.`
          : `${ack} ${cap(action)}.`
        : `${cap(action)}.`;
    // Never the same opening twice in a row, or three turns apart.
    if (!recent.includes(line)) {
      recent.push(line);
      if (recent.length > 4) recent.shift();
      return line;
    }
  }
  return `${cap(pick(actions))}.`;
};
const fillerKind = text => {
  const t = String(text || "").toLowerCase();
  if (/\b(inbox|unread|repl(y|ies)|email(s)? (i|we) got)\b/.test(t)) return "inbox";
  if (/\b(find|search|look for|list|prospect|people|companies|leads|who)\b/.test(t)) return "search";
  if (/\b(write|draft|sequence|email|copy|template|message)\b/.test(t)) return "write";
  if (/\b(report|how many|performance|rate|numbers|stats|this week|last week)\b/.test(t)) return "report";
  if (/\b(research|tell me about|what do you know|account|company)\b/.test(t)) return "research";
  return "generic";
};
// "Stop" has to act on the spot: it is what people say when the copilot is
// talking over them or heading the wrong way. Matched here, in the browser,
// so nothing waits on a round trip.
const STOP_PHRASES = new RegExp(
  "^(ok(ay)?|alright|right|yeah|yep)?[,.]?\\s*" +
    "(stop( it| listening| talking)?|be quiet|quiet|shut up|" +
    "that( i|'|\u2019)?s (all|enough|it)|enough( of that)?|cancel( that)?|" +
    "n(ever ?mind|vm)|exit|quit|end (the )?(call|chat|conversation)|" +
    "(thanks|thank you)(,? that'?s all)?|bye|goodbye|" +
    "(we|i)( a|')?re done|(we|i) am done|(we|i)'?m done)" +
    "[,.!]?\\s*(thanks|thank you|bye)?[.!]?$",
  "i"
);
export const isStopPhrase = text => STOP_PHRASES.test(String(text || "").trim().replace(/\s+/g, " "));

/** Acknowledge a spoken message out loud. Talk mode only. */
export const speakFiller = text => {
  if (!session || getPrefs().readAloud === "never") return;
  speak(pickFiller(fillerKind(text)), true);
};

/**
 * Streaming narration. Sentences are spoken as the text block completes
 * them, a step label fills the silence while a tool runs, and turnEnd
 * says whatever is left plus the open question. Only in talk mode.
 */
const turns = new Map(); // turnId -> { spokenChars, said }
const SENTENCE_END = /[.!?](\s|$)/g;
export const narrator = {
  start(turnId) {
    if (!session || getPrefs().readAloud === "never") return;
    const t = { spokenChars: 0, said: false, fillers: 0 };
    turns.set(turnId, t);
    // A long turn (research, a big search) gets one more nudge so the line
    // does not go silent for twenty seconds.
    t.hold = setInterval(() => {
      if (!session || t.said || isSpeaking() || t.fillers >= 2) return;
      t.fillers += 1;
      enqueue(pickFiller("wait"), true);
    }, 9000);
  },
  // Text so far for this turn's text block; speak the newly completed sentences.
  text(turnId, markdown) {
    const t = turns.get(turnId);
    if (!t || !session) return;
    let last = t.spokenChars;
    SENTENCE_END.lastIndex = 0;
    let m;
    while ((m = SENTENCE_END.exec(markdown))) last = m.index + 1;
    if (last <= t.spokenChars) return;
    const chunk = markdown.slice(t.spokenChars, last);
    t.spokenChars = last;
    if (!t.said) {
      // First real words: cut any filler still playing.
      stopSpeaking();
      t.said = true;
    }
    enqueue(chunk);
  },
  // A tool started: say what, once, so the pause is not dead air.
  step(turnId, label) {
    const t = turns.get(turnId);
    if (!t || !session || t.said || isSpeaking() || !label) return;
    enqueue(`${label.replace(/\.+$/, "")}.`, true);
  },
  end(turnId, message) {
    const t = turns.get(turnId);
    if (t && t.hold) clearInterval(t.hold);
    turns.delete(turnId);
    if (!session) return;
    const blocks = (message && message.blocks) || [];
    const text = blocks.find(b => b.type === "text" && b.markdown);
    if (t && text) {
      const rest = text.markdown.slice(t.spokenChars).trim();
      // Tail without terminal punctuation, and anything we never got to.
      if (rest && rest.length > 2) enqueue(rest);
      // A table-only turn: say the count.
      if (!t.said && !rest) enqueue(spokenLineFor(message));
    } else if (!t || !t.said) {
      enqueue(spokenLineFor(message));
      return;
    }
    // The question or choice the turn is waiting on, always.
    const parts = [];
    const strip = x => String(x || "").replace(/[*_`#>]/g, "").trim();
    const form = blocks.find(b => b.type === "question_form" && !b.done);
    if (form && form.questions && form.questions[0]) parts.push(strip(form.questions[0].question));
    const choices = blocks.find(b => b.type === "quick_replies" && !b.selectedValue);
    if (choices && choices.prompt) parts.push(strip(choices.prompt));
    const error = blocks.find(b => b.type === "error_cta");
    if (error && error.title) parts.unshift(strip(error.title));
    const extra = parts.join(" ");
    if (extra && !(text && text.markdown.includes(extra))) enqueue(extra);
  }
};

/**
 * What to read aloud for a finished assistant turn. No model call: the
 * first prose sentence or two, stripped of markdown, plus the question when
 * the turn is waiting on the user. Tables and cards are for the eyes.
 */
export const spokenLineFor = message => {
  const blocks = (message && message.blocks) || [];
  const strip = s =>
    String(s || "")
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/[*_`#>]/g, "")
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
      .replace(/\s+/g, " ")
      .trim();
  const parts = [];
  const text = blocks.find(b => b.type === "text" && strip(b.markdown));
  if (text) {
    const sentences = strip(text.markdown).match(/[^.!?]+[.!?]?/g) || [];
    parts.push(sentences.slice(0, 2).join(" ").trim());
  } else {
    const table = blocks.find(b => b.type === "table_ref");
    if (table && table.totalCount) parts.push(`Found ${Number(table.totalCount).toLocaleString()} matches.`);
  }
  const form = blocks.find(b => b.type === "question_form" && !b.done);
  if (form && form.questions && form.questions[0]) parts.push(strip(form.questions[0].question));
  const choices = blocks.find(b => b.type === "quick_replies" && !b.selectedValue);
  if (choices && choices.prompt) parts.push(strip(choices.prompt));
  const error = blocks.find(b => b.type === "error_cta");
  if (error && error.title) parts.unshift(strip(error.title));
  return parts.filter(Boolean).join(" ").slice(0, 320);
};
